import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/**
 * MetricChart component for displaying time series data
 * @param {Object} props - Component props
 * @param {string} props.title - Chart title
 * @param {Array} props.data - Chart data array
 * @param {string} props.dataKey - Key for X-axis
 * @param {string} props.valueKey - Key for Y-axis values
 * @param {string} props.color - Chart color theme
 * @returns {JSX.Element} MetricChart component
 */
const MetricChart = ({
  title,
  data = [],
  dataKey = 'date',
  valueKey = 'value',
  color = '#3b82f6',
}) => {
  // Format data for display
  const formattedData = data.map(item => ({
    ...item,
    date: item.date
      ? new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : item[dataKey],
    value: item[valueKey] || item.value || 0,
  }));

  // Calculate total for display
  const total = formattedData.reduce((sum, item) => sum + (item.value || 0), 0);

  // Create a safe gradient ID from title
  const gradientId = `gradient-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mt-1">
          {total.toLocaleString()}
        </p>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={formattedData}
          margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
            }}
            labelStyle={{ color: '#374151', fontWeight: '600' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MetricChart;
